import React, {useMemo} from 'react';
import {observer} from 'mobx-react-lite';
import {useStores} from 'src/components/store';
import {Direction} from 'src/utils/enums';
import {AppWithStatus} from '../../../dtos/app.dto';
import {Toggle} from '../../shared/toggle/toggle';
import {Tooltip} from 'antd';
import CustomIcon, {IconType} from '../../shared/custom-icon/custom-icon';
import {RemoveAppAction} from './service-item-action/remove-app-action';
import {RuleHelper} from '../../../utils/rule-helper';
import {DependenciesWarningModalProps} from './warnings/dependencies-warning-modal';
import {TooltipDisableReason} from '../../shared/tooltip-disable-message/tooltip-disable-reason';
import {dependentAppsTooltip} from './managed-apps-select/dependent-apps-tooltip';

interface AppItemProps {
    managedApps: AppWithStatus[]
    app: AppWithStatus,
    updateApp: (id: string, direction: Direction, isAllow: boolean) => Promise<void>,
    setDependenciesWarningModalData: (data: DependenciesWarningModalProps) => void;
    hideRemove?: boolean;
}

export const AppItem: React.FC<AppItemProps> = observer(({
                                                             managedApps,
                                                             app,
                                                             updateApp,
                                                             setDependenciesWarningModalData,
                                                             hideRemove
                                                         }) => {
    const { fwVersionStore } = useStores();

    const appInfoTooltip = useMemo(() => {
        return <div className="tooltip-content">
            <div className="title">{app.displayName}</div>
            <div className="description">{app.description}</div>
        </div>;
    }, [app.displayName, app.description]);

    const appToggleChange = (direction: Direction, isAllow: boolean) => {

        const accessor = direction === Direction.Inbound ? 'inboundAllowed' : 'outboundAllowed';
        const updateAppCb = () => updateApp(app.id, direction, isAllow);
        const actionMethod = isAllow ? RuleHelper.allowApp : RuleHelper.blockApp;
        actionMethod(app, managedApps, updateAppCb, accessor, setDependenciesWarningModalData);
    };

    return <div key={app.id} className="apps-item card" data-testid="app-item">
        <div className="titles">
            <div className={`app-name`}>{app.displayName}</div>
        </div>
        <div className={'buttons'}>
            {app.dependencies.length > 0 && <Tooltip placement={'bottom'} title={dependentAppsTooltip(app.dependencies)}
                overlayClassName={'service-item-tooltip app-dependencies-tooltip'} color={'white'}>
                <span className="button-wrapper black icon">
                    <CustomIcon type={IconType.LINK} />
                </span>
            </Tooltip>}
            <Tooltip placement={'bottom'} title={appInfoTooltip}
                overlayClassName={'app-item-tooltip'} color={'white'}>
                <span className="button-wrapper black icon">
                    <CustomIcon type={IconType.INFO} />
                </span>
            </Tooltip>
            {!hideRemove && <><span className={'vertical-separator'}/>
            <TooltipDisableReason disableReason={fwVersionStore.getDisableReason}>
                <RemoveAppAction managedApps={fwVersionStore.managedApps} app={app}
                                 isDisabled={!!fwVersionStore.getDisableReason}/>
            </TooltipDisableReason></>}
        </div>
        <div className={`outbound-inactive-${false} inbound-inactive-${false} toggle-buttons`}>
            <Toggle value={app.inboundAllowed}
                disableReason={fwVersionStore.getDisableReason}
                onChange={(isAllow: boolean) => appToggleChange(Direction.Inbound, isAllow)}
                withIcon={true}
                inactiveStateIcon={false} title={'Service Inactive'} />

            <Toggle value={app.outboundAllowed}
                disableReason={fwVersionStore.getDisableReason}
                onChange={(isAllow: boolean) => appToggleChange(Direction.Outbound, isAllow)}
                withIcon={true}
                inactiveStateIcon={false} title={'Service Inactive'} />
        </div>
    </div>;
});